/**
 * Call history (plan E03-T01): ended calls, newest first, with filters kept in the URL (a filtered
 * view can be shared or bookmarked), a page at a time, each row opening the call's inspector.
 * A viewer sees the numbers masked (the API does it).
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { Page, useApiQuery, useDenied } from "../app/Page";
import { useBff, useMe } from "../app/session";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { Empty, QueryView } from "../ui/states";
import { Table } from "../ui/Table";
import { LiveNow } from "./live";

type Call = Schemas["CallCompleted"];
const PAGE = 50;
const STATUSES = ["completed", "transferred", "voicemail", "machine", "busy", "no_answer", "canceled", "rejected", "blocked", "failed"] as const;

export interface CallFilters {
  direction?: "inbound" | "outbound";
  status?: string;
  agent?: string;
  number?: string;
  from?: string; // ISO with a zone
  to?: string;
}

/** The filters from the URL's search, keeping only what the API accepts. */
export function parseFilters(search: Record<string, unknown>): CallFilters {
  const str = (k: string) => (typeof search[k] === "string" && search[k] ? (search[k] as string) : undefined);
  const direction = str("direction");
  const status = str("status");
  const number = str("number");
  return {
    direction: direction === "inbound" || direction === "outbound" ? direction : undefined,
    status: status && (STATUSES as readonly string[]).includes(status) ? status : undefined,
    agent: str("agent")?.match(/^agt_[0-9a-z]{26}$/) ? str("agent") : undefined,
    number: number && /^\+[1-9]\d{6,14}$/.test(number) ? number : undefined,
    from: str("from") && !Number.isNaN(Date.parse(str("from") as string)) ? str("from") : undefined,
    to: str("to") && !Number.isNaN(Date.parse(str("to") as string)) ? str("to") : undefined,
  };
}

export function CallsPage() {
  const t = useT();
  const format = useFormat();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const denied = useDenied();
  const filters = parseFilters(useSearch({ strict: false }) as Record<string, unknown>);
  const agents = useApiQuery("agents", (a) => a.GET("/v1/agents", { params: { query: { limit: 100 } } }));
  const agentName = (id: string) => agents.data?.data.find((a) => a.id === id)?.name ?? id;

  const calls = useInfiniteQuery({
    queryKey: ["calls", tenant, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      data(api.GET("/v1/calls", {
        params: { query: {
          limit: PAGE, cursor: pageParam, direction: filters.direction, status: filters.status,
          agent_id: filters.agent, number: filters.number, created_after: filters.from, created_before: filters.to,
        } },
      })),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const filtered = Object.values(filters).some(Boolean);
  return (
    <Page title={t("nav.calls")} description={t("calls.description")}>
      <LiveNow />
      <h2 className="text-lg font-semibold">{t("calls.history")}</h2>
      <Filters filters={filters} agents={agents.data?.data ?? []} />
      <QueryView query={calls} denied={denied}>
        {(pages) => {
          const rows = pages.pages.flatMap((p) => p.data);
          if (rows.length === 0)
            return (
              <Empty title={t(filtered ? "calls.noneMatch" : "calls.empty")}>
                <p>{t(filtered ? "calls.noneMatchBody" : "calls.emptyBody")}</p>
              </Empty>
            );
          return (
            <div className="space-y-3">
              <Table
                caption={t("nav.calls")}
                rows={rows}
                rowKey={(c) => c.call_id}
                columns={[
                  {
                    header: t("calls.col.when"),
                    cell: (c: Call) => (
                      <Link to="/calls/$callId" params={{ callId: c.call_id }} className="text-[var(--color-primary)] underline">
                        {format.dateTime(c.timing.created_at)}
                      </Link>
                    ),
                  },
                  { header: t("calls.col.direction"), cell: (c: Call) => t(`calls.direction.${c.direction}`) },
                  { header: t("calls.col.from"), cell: (c: Call) => <span className="font-mono">{c.from}</span> },
                  { header: t("calls.col.to"), cell: (c: Call) => <span className="font-mono">{c.to}</span> },
                  { header: t("calls.col.agent"), cell: (c: Call) => (c.agent ? `${agentName(c.agent.id)} v${c.agent.version}` : "—") },
                  { header: t("calls.col.status"), cell: (c: Call) => statusLabel(t, c.status) },
                  { header: t("calls.col.duration"), cell: (c: Call) => format.duration(c.timing.duration_ms) },
                  {
                    header: t("calls.col.cost"),
                    cell: (c: Call) => (c.cost ? t("calls.cost", { amount: format.money(c.cost.total, c.cost.currency) }) : "—"),
                  },
                ]}
              />
              {calls.hasNextPage && (
                <Button variant="secondary" onClick={() => void calls.fetchNextPage()} disabled={calls.isFetchingNextPage}>
                  {t(calls.isFetchingNextPage ? "app.loading" : "calls.more")}
                </Button>
              )}
            </div>
          );
        }}
      </QueryView>
    </Page>
  );
}

const STATUS_LABEL: Record<string, MessageId> = {
  completed: "calls.status.completed",
  transferred: "calls.status.transferred",
  busy: "calls.status.busy",
  no_answer: "calls.status.no_answer",
  canceled: "calls.status.canceled",
  rejected: "calls.status.rejected",
  blocked: "calls.status.blocked",
  failed: "calls.status.failed",
  voicemail: "calls.status.voicemail",
  machine: "calls.status.machine",
};

export function statusLabel(t: ReturnType<typeof useT>, status: string): string {
  const id = STATUS_LABEL[status];
  return id ? t(id) : status;
}

/** The filters' form: applied to the URL on submit, so the back button walks through searches. */
function Filters({ filters, agents }: { filters: CallFilters; agents: Schemas["AgentOut"][] }) {
  const t = useT();
  const navigate = useNavigate();
  const id = useId();
  const [draft, setDraft] = useState({
    direction: filters.direction ?? "", status: filters.status ?? "", agent: filters.agent ?? "",
    number: filters.number ?? "", from: toLocal(filters.from), to: toLocal(filters.to),
  });
  const numberInvalid = draft.number !== "" && !/^\+[1-9]\d{6,14}$/.test(draft.number);
  const set = (key: keyof typeof draft) => (e: { target: { value: string } }) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const apply = (e: FormEvent) => {
    e.preventDefault();
    if (numberInvalid) return;
    const search = Object.fromEntries(
      Object.entries({ ...draft, from: toIso(draft.from), to: toIso(draft.to) }).filter(([, v]) => v),
    );
    void navigate({ to: "/calls", search });
  };
  const clear = () => {
    setDraft({ direction: "", status: "", agent: "", number: "", from: "", to: "" });
    void navigate({ to: "/calls", search: {} });
  };
  const field = "rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm";
  return (
    <form onSubmit={apply} role="search" aria-label={t("calls.filters")} className="flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <label htmlFor={`${id}-dir`} className="block text-xs font-medium">{t("calls.col.direction")}</label>
        <select id={`${id}-dir`} value={draft.direction} onChange={set("direction")} className={field}>
          <option value="">{t("calls.any")}</option>
          <option value="inbound">{t("calls.direction.inbound")}</option>
          <option value="outbound">{t("calls.direction.outbound")}</option>
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-status`} className="block text-xs font-medium">{t("calls.col.status")}</label>
        <select id={`${id}-status`} value={draft.status} onChange={set("status")} className={field}>
          <option value="">{t("calls.any")}</option>
          {STATUSES.map((s) => <option key={s} value={s}>{t(STATUS_LABEL[s] as MessageId)}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-agent`} className="block text-xs font-medium">{t("calls.col.agent")}</label>
        <select id={`${id}-agent`} value={draft.agent} onChange={set("agent")} className={field}>
          <option value="">{t("calls.any")}</option>
          {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-number`} className="block text-xs font-medium">{t("calls.number")}</label>
        <input id={`${id}-number`} value={draft.number} onChange={set("number")} placeholder="+15550100100"
          aria-invalid={numberInvalid || undefined} aria-describedby={numberInvalid ? `${id}-number-error` : undefined}
          className={`${field} w-40 font-mono`} inputMode="tel" autoComplete="off" />
        {numberInvalid && <p id={`${id}-number-error`} className="text-xs text-[var(--color-danger)]">{t("calls.numberRule")}</p>}
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-from`} className="block text-xs font-medium">{t("calls.from")}</label>
        <input id={`${id}-from`} type="datetime-local" value={draft.from} onChange={set("from")} className={field} />
      </div>
      <div className="space-y-1">
        <label htmlFor={`${id}-to`} className="block text-xs font-medium">{t("calls.to")}</label>
        <input id={`${id}-to`} type="datetime-local" value={draft.to} onChange={set("to")} className={field} />
      </div>
      <Button type="submit">{t("calls.apply")}</Button>
      <Button variant="ghost" onClick={clear}>{t("calls.clear")}</Button>
    </form>
  );
}

/** A datetime-local value (the reader's own time) to ISO with a zone, and back. */
function toIso(local: string): string {
  return local ? new Date(local).toISOString() : "";
}

function toLocal(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
